import { getGradientLegendDisplayValues } from '../../../utils/charts/legendValues';
import { HEATMAP_VALUE_FORMATS } from '../../../utils/charts/heatmapValueFormat';
import FilterComponent from '../../../components/charts/filter/ChartFilter';
import { fetchParcelHeatmapData } from '../../../utils/api/parcelHeatmap';
import Legend from '../../../components/containers/legendController';
import { US_STATE_OPTIONS } from '../../../utils/config/usStates';
import getChartType from '../../../utils/helpers/getChartType';
import useSelectData from '../../../hooks/data/useSelectData';
import React, {useCallback, useEffect, useState} from 'react';
import Sidebar from '../../../components/sidebar/Sidebar';
import NavBar from '../../../components/navbar/NavBar';
import { config } from '../../../config/Constants';
import { CircularProgress } from '@mui/material';
import Cookies from 'js-cookie';
import './parcelHeatmap.css';
import Axios from 'axios';

const CURRENCY_REPORT_TYPES = new Set([ 'PARCEL TOTAL COST', 'PARCEL AVERAGE COST']);

const ParcelHeatmap = () => {
  const [chart, setChart] = useState(null);
  const [data, setData] = useState([]);
  const [legend, setLegend] = useState();
  const [selectedFilter, setSelectedFilter] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const company = Cookies.get('cp-cn');

  const fetchReport = useCallback(async() => {
    if (!selectedFilter['Report Type'] || !selectedFilter['Origin Type']) {
      setData([]);
      setChart(null);
      setLegend(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetchParcelHeatmapData({ company, selectedFilter });
      setData(response);
    } catch (error) {
      console.log('Error fetching parcel heatmap report', error);
      setData([]);
      setChart(null);
      setLegend(null);
      setIsLoading(false);
    }
  }, [company, selectedFilter]);

  const getLegend = useCallback(async() => {
    const values = data.map(item => item.VALUE);
    const response = await Axios.get(config.url.GET_LEGEND, {
      params: { values: values, scaleType: 'PERCENTILE_CAP', }
    });
    setLegend(response.data);
    return response.data;
  }, [data]);

  const getValueFormat = useCallback(() => {
    const reportType = selectedFilter['Report Type'];
    const isAverageCost = reportType === 'PARCEL AVERAGE COST';

    return {
      style: CURRENCY_REPORT_TYPES.has(reportType) ? HEATMAP_VALUE_FORMATS.CURRENCY : HEATMAP_VALUE_FORMATS.NUMBER,
      compact: true,
      ...(isAverageCost
        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        : {}),
    };
  }, [selectedFilter]);

  useEffect(() => {
    const loadChart = async () => {
      if (!data || data.length === 0) {
        setChart(null);
        setLegend(null);
        return;
      }

      try {
        const legend = await getLegend();
        const returnChart = await getChartType({
          chartInfo: [{ TYPE: 'USA_HEATMAP' }],
          report: { data: data },
          legend: legend,
          valueFormat: getValueFormat()
        });
        setChart(returnChart);
      } catch (error) {
        console.log('Error loading parcel heatmap chart', error);
        setChart(null);
        setLegend(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadChart();
  }, [data, getLegend, getValueFormat]);

  const reportType = useSelectData({
    selectValues: 'FILTER_NAME AS OPTION_NAME',  tableName: 'REPORTS.REPORT_FILTER_TYPE', 
    whereField: 'REPORT_NAME',  whereValue: 'Parcel Heatmap'
  });

  const filterReportType = [{
    FILTER_NAME: "Report Type",
    options: reportType.map(item => ({ OPTION_NAME: item.OPTION_NAME}))
  }];

  const filterOriginType = [{
    FILTER_NAME: "Origin Type",
    options: US_STATE_OPTIONS
  }];

  const legendSubtitle = selectedFilter['Origin Type'] || 'All origins';

  const handleFilterChange = (filterName, value) => {
    setSelectedFilter(prev => ({ ...prev, [filterName]: value }));
  };

  useEffect(() => {  fetchReport() ; }, [fetchReport]);

  const legendValues = getGradientLegendDisplayValues(legend);
  const hasSelectedReport = Boolean(selectedFilter['Report Type']);
  const hasSelectedOrigin = Boolean(selectedFilter['Origin Type']);

  return (
    <div className="parcelheatmapPage">
      <div className="LandingPageContainer">
        <NavBar />
        <Sidebar />
        <div className="parcelheatmapContent">
          <div className="parcelheatmapTitle">
            <h1>Parcel Heatmap</h1>
          </div>
          <div className="parcelHeatmapBody">
            <div className="parcelHeatmap">
              <div className="parcelHeatmapMapHeader">
                <div>
                  <div className="parcelHeatmapPanelEyebrow">Destination View</div>
                  <h2>Shipment Distribution</h2>
                </div>
                <span>{selectedFilter['Report Type'] || 'Select report'}</span>
              </div>
              <div className="parcelHeatmapMapBody">
                {isLoading && (
                  <div className="parcelHeatmapStatus">
                    <CircularProgress size={36} thickness={4} />
                    <span>Loading report...</span>
                  </div>
                )}
                {!isLoading && chart}
                {!isLoading && !chart && (
                  <div className="parcelHeatmapStatus parcelHeatmapStatusMessage">
                    <span>
                      {hasSelectedReport
                        ? hasSelectedOrigin
                          ? 'No data available for the selected filters.'
                          : 'Use the filters to select origin.'
                        : 'Use the filters to select report.'}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="parcelHeatmapRight">
              <div className="parcelHeatmapFilter">
                <div className="parcelHeatmapFilterHeader">
                  <div>
                    <div className="parcelHeatmapPanelEyebrow">Report Controls</div>
                    <h3>Filter Options</h3>
                  </div>
                  <span>{Object.keys(selectedFilter).length || 0} active</span>
                </div>
                <div className="parcelHeatmapFilterOptions">
                  <FilterComponent
                    filters={filterReportType}
                    handleFilterChange={handleFilterChange}
                    selectedFilters={selectedFilter}
                  />
                  <FilterComponent
                    filters={filterOriginType}
                    handleFilterChange={handleFilterChange}
                    selectedFilters={selectedFilter}
                  />
                </div>
              </div>
              {legend && (
              <div className="parcelHeatmapLegend">
                <div className="parcelHeatmapLegendHeader">
                  <div>
                    <div className="parcelHeatmapLegendEyebrow">Heatmap Legend</div>
                    <h3>Value Range</h3>
                  </div>
                  <span>{legendSubtitle}</span>
                </div>
                <div className="parcelHeatmapLegendBody">
                <Legend 
                  type="GRADIENT" 
                  colorRange={legend.colorRange}
                  minValue={legendValues.minValue}
                  midValue={legendValues.midValue}
                  maxValue={legendValues.maxValue}
                  valueFormat={getValueFormat()}
                />
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ParcelHeatmap
